import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { Feature } from '@weave/schema-gen-ts/dist/schemas/search/v1/service.pb';
import { Entity } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/entity.pb';
import Fuse from 'fuse.js';
import { getUser } from '@frontend/auth-helpers';
import {
  GlobalSearch,
  GlobalSearchModuleOrder,
  GlobalSearchTrackingIds,
  useGlobalSearch,
  useGlobalRecentSearches,
} from '@frontend/global-search';
import { Icon, IconName } from '@frontend/icons';
import type { NavItem } from '@frontend/nav-primitives';
import {
  formatResultsPrimaryKeys,
  handleRegisterClick,
  handleUpdateSearch,
  useSearchHistoryShallowStore,
} from '@frontend/search-history';
import { pendo } from '@frontend/tracking';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { NakedButton, SkeletonLoader, Text, styles } from '@frontend/design-system';
import { usePrimaryNavContent } from '../../layout/global-nav/nav-content';

type TransformedNavConfig = {
  label: string;
  altSearchLabel?: string;
  path: string;
  iconName: IconName;
  feature: string;
};

function transformNavConfig(navConfig: NavItem[]) {
  const flattenedItems: TransformedNavConfig[] = [];

  navConfig.forEach((item) => {
    if (item.show !== false) {
      item.subNavItems.forEach((subItem) => {
        const altSearchLabel = subItem.altSearchLabel ?? '';
        const label = typeof subItem.label === 'string' ? subItem.label : altSearchLabel;
        flattenedItems.push({
          label,
          altSearchLabel: subItem?.altSearchLabel,
          path: item.root + subItem.relativePath,
          iconName: item.iconName,
          feature: item.label,
        });
      });
    }
  });

  return flattenedItems;
}

const fuseOptions = {
  keys: ['label', 'feature', 'altSearchLabel'],
  includeScore: true,
  shouldSort: true,
  threshold: 0.3,
  location: 0,
  distance: 100,
  includeMatches: true,
};

const defaultMaxResults = 4;
const entity = Entity.ENTITY_FEATURE;

// TODO: remove this old module when semantic-search FF is fully implemented
export const FeatureGlobalSearchModuleOld = () => {
  const navigate = useNavigate();
  const primaryNavContent = usePrimaryNavContent();

  const flattenedNav = transformNavConfig(primaryNavContent as unknown as NavItem[]);
  const fuse = new Fuse<TransformedNavConfig>(flattenedNav, fuseOptions);

  const { debouncedSearchTerm, viewAllFeature, close } = useGlobalSearch([
    'debouncedSearchTerm',
    'viewAllFeature',
    'close',
  ]);

  const { addSearch } = useGlobalRecentSearches();

  const searchedFeatures = useMemo(() => {
    return fuse.search(debouncedSearchTerm).map((result) => result.item);
  }, [debouncedSearchTerm]);

  const { searchId } = useSearchHistoryShallowStore('searchId');

  useEffect(() => {
    handleUpdateSearch({
      entity,
      results: formatResultsPrimaryKeys<TransformedNavConfig>('path', searchedFeatures),
    });
  }, [searchId, searchedFeatures]);

  const maxResults = viewAllFeature === 'features' ? searchedFeatures : searchedFeatures.slice(0, defaultMaxResults);

  return (
    <GlobalSearch.Section.Content
      showViewAll={searchedFeatures.length > defaultMaxResults}
      hasResults={!!searchedFeatures.length}
      count={searchedFeatures.length}
    >
      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          gap: ${theme.spacing(1)};
        `}
      >
        {maxResults.length ? (
          maxResults.map((featureItem, index) => {
            return (
              <NakedButton
                key={index}
                trackingId={GlobalSearchTrackingIds.searchResultItem('features')}
                onClick={() => {
                  addSearch(debouncedSearchTerm, 'features');
                  navigate({ to: featureItem.path });
                  handleRegisterClick({
                    entity,
                    entityId: featureItem.path,
                    entityPosition: GlobalSearchModuleOrder.features,
                    itemPosition: index,
                  });
                  close();
                }}
                css={css`
                  display: block;
                  border: 1px solid ${theme.colors.neutral10};
                  border-radius: ${theme.borderRadius.small};
                  padding: ${theme.spacing(2)};
                  min-width: 234px;
                  flex: 1;
                  transition: background-color 0.3s ease;
                  background-color: ${theme.colors.white};
                  :hover {
                    background-color: ${theme.colors.neutral5};
                  }
                `}
              >
                <FeatureSearchItemOld
                  feature={featureItem.feature}
                  label={featureItem.label}
                  iconName={featureItem.iconName}
                />
              </NakedButton>
            );
          })
        ) : (
          <GlobalSearch.EmptyState />
        )}
      </div>
    </GlobalSearch.Section.Content>
  );
};

// TODO: remove this old module when semantic-search FF is fully implemented
export const FeatureSearchItemOld = ({
  feature,
  label,
  iconName,
}: {
  feature: string;
  label: string;
  iconName: IconName;
}) => {
  return (
    <>
      <div
        role='text'
        css={css`
          width: 100%;
          display: flex;
          align-items: center;
          gap: ${theme.spacing(2)};
          svg {
            width: 24px;
            height: 24px;
          }
        `}
      >
        <Icon name={iconName} size={20} />
        <div
          css={css`
            overflow: hidden;
          `}
        >
          <Text css={styles.truncate} textAlign='left'>
            {label}
          </Text>
          <Text size='small' textAlign='left' color='subdued'>
            {feature}
          </Text>
        </div>
      </div>
    </>
  );
};

type Props = {
  isLoading: boolean;
  searchedFeatures: Feature[];
};

export const FeatureGlobalSearchModule = ({ isLoading, searchedFeatures }: Props) => {
  const navigate = useNavigate();
  const userID = getUser()?.userID || '';

  const { debouncedSearchTerm, viewAllFeature, close } = useGlobalSearch([
    'debouncedSearchTerm',
    'viewAllFeature',
    'close',
  ]);

  const { addSearch } = useGlobalRecentSearches();

  const maxResults = viewAllFeature === 'features' ? searchedFeatures : searchedFeatures.slice(0, defaultMaxResults);

  if (isLoading) {
    return (
      <div css={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
        {Array.from({ length: defaultMaxResults }).map((_, index) => (
          <FeatureItemSkeletonLoader key={index} />
        ))}
      </div>
    );
  }

  return (
    <GlobalSearch.Section.Content
      showViewAll={searchedFeatures.length > defaultMaxResults}
      hasResults={!!searchedFeatures.length}
      count={searchedFeatures.length}
    >
      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          gap: ${theme.spacing(1)};
        `}
      >
        {maxResults.length ? (
          maxResults.map((featureItem, index) => {
            return (
              <NakedButton
                key={index}
                trackingId={GlobalSearchTrackingIds.searchResultItem('features')}
                onClick={() => {
                  addSearch(debouncedSearchTerm, 'features');
                  navigate({ to: featureItem.path });
                  handleRegisterClick({
                    entity,
                    entityId: featureItem.id,
                    entityPosition: GlobalSearchModuleOrder.features,
                    itemPosition: index,
                  });

                  // Track the click event with Pendo
                  pendo.track('global_semantic_search_query_click', {
                    clickedSettingId: featureItem.id,
                    clickedSettingPath: featureItem.path,
                    entity: [Entity.ENTITY_FEATURE],
                    query: debouncedSearchTerm,
                    timestamp: new Date().toISOString(),
                    userId: userID,
                  });
                  close();
                }}
                css={css`
                  display: flex;
                  border: 1px solid ${theme.colors.neutral10};
                  border-radius: ${theme.borderRadius.small};
                  padding: ${theme.spacing(2)};
                  min-width: 234px;
                  flex: 1;
                  transition: background-color 0.3s ease;
                  background-color: ${theme.colors.white};
                  :hover {
                    background-color: ${theme.colors.neutral5};
                  }
                `}
              >
                <FeatureSearchItem {...featureItem} />
              </NakedButton>
            );
          })
        ) : (
          <GlobalSearch.EmptyState />
        )}
      </div>
    </GlobalSearch.Section.Content>
  );
};

export const FeatureSearchItem = ({ description, iconName, name }: Feature) => (
  <div
    role='text'
    css={css`
      width: 100%;
      display: flex;
      gap: ${theme.spacing(2)};
      svg {
        min-width: 24px;
        min-height: 24px;
      }
    `}
  >
    <div
      css={css`
        display: flex;
        align-items: center;
      `}
    >
      <Icon name={iconName as IconName} size={20} />
    </div>
    <div css={{ overflow: 'hidden' }}>
      <Text css={styles.truncate} textAlign='left'>
        {name}
      </Text>
      <Text size='small' textAlign='left' color='subdued'>
        {description}
      </Text>
    </div>
  </div>
);

const FeatureItemSkeletonLoader = () => {
  return (
    <div
      css={{
        minWidth: 234,
        border: `1px solid ${theme.colors.neutral10}`,
        borderRadius: theme.borderRadius.small,
        padding: theme.spacing(2),
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
      }}
    >
      <SkeletonLoader css={{ flexShrink: 0 }} width={24} height={24} />
      <div css={{ width: '100%' }}>
        <SkeletonLoader css={{ marginBottom: theme.spacing(0.5) }} height={20} width='40%' />
        <SkeletonLoader width='100%' height={18} css={{ marginBottom: theme.spacing(0.5) }} />
        <SkeletonLoader width='100%' height={18} css={{ marginBottom: theme.spacing(0.5) }} />
        <SkeletonLoader width='100%' height={18} />
      </div>
    </div>
  );
};
